'use client';

/**
 * Une VIDÉO DE DÉMONSTRATION rangée au dossier d'un appareil (Ines, 03/11/2026).
 *
 * La vidéo n'est pas versée : c'est une référence externe (nature `video` des
 * pièces d'équipement), enregistrée comme une notice publiée sur un portail —
 * un lien, tracé au journal, sans circuit de validation.
 *
 * ⚠️ RIEN NE SE CHARGE AVANT LE CLIC. Le lecteur YouTube pèse plusieurs
 * centaines de kilo-octets et dépose ses traceurs : posé d'office, il
 * alourdirait chaque ouverture de la fenêtre d'acquittement pour une vidéo
 * qu'on regarde une fois. On montre un bouton ; le lecteur (`youtube-nocookie`)
 * n'est inséré qu'à la demande. Une adresse qui n'est pas YouTube s'ouvre dans
 * un onglet : on ne sait pas l'intégrer sans risque.
 */
import { useState } from 'react';
import { I } from '@/components/ui';
import { Bouton } from '@/shared/Bouton';

/** L'identifiant YouTube d'une adresse, sous ses formes courantes
 *  (youtu.be/…, watch?v=…, /embed/…, /shorts/…), ou null. */
export function idYoutube(url: string): string | null {
  try {
    const u = new URL(url);
    const hote = u.hostname.replace(/^www\.|^m\./, '');
    let id: string | null = null;
    if (hote === 'youtu.be') id = u.pathname.slice(1).split('/')[0];
    else if (hote === 'youtube.com' || hote === 'youtube-nocookie.com') {
      id = u.searchParams.get('v') || (/^\/(?:embed|shorts|live)\/([^/?#]+)/.exec(u.pathname)?.[1] ?? null);
    }
    return id && /^[\w-]{6,20}$/.test(id) ? id : null;
  } catch {
    return null;
  }
}

export function VideoDemonstration({ url, libelle }: { url: string; libelle?: string }) {
  const [ouverte, setOuverte] = useState(false);
  const id = idYoutube(url);
  const titre = libelle || 'Vidéo de démonstration';

  if (!id) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="lien-doux"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5, fontWeight: 600, color: 'var(--color-primary-active)' }}>
        <I n="play" s={13} />{titre} ↗
      </a>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 6, flex: '1 1 100%' }}>
      <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Bouton taille="sm" type="button" onClick={() => setOuverte((o) => !o)} aria-expanded={ouverte}>
          <I n="play" s={13} />{ouverte ? 'Masquer la vidéo' : titre}
        </Bouton>
        <a href={url} target="_blank" rel="noreferrer" className="lien-doux"
          style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>ouvrir sur YouTube ↗</a>
      </span>
      {ouverte && (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`}
          title={titre}
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
          style={{ width: '100%', maxWidth: 720, aspectRatio: '16 / 9', border: 0, borderRadius: 'var(--radius-md)', background: 'var(--surface-sunken)' }}
        />
      )}
    </div>
  );
}
